import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { rupiah, rupiahShort } from "@/lib/format";
import StatCard from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Receipt, TrendingUp, Wallet, Users, Coins, Banknote, ArrowLeftRight,
  Plus, FileText, Zap, Eye, EyeOff, AlertCircle,
} from "lucide-react";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid,
} from "recharts";

const QUICK_ACTIONS = [
  { label: "Transaksi Baru", icon: Zap, to: "/transactions?new=1", primary: true, key: "trx-new" },
  { label: "Pengeluaran", icon: Wallet, to: "/expenses?new=1", key: "expense-new" },
  { label: "Tambah Utang", icon: Users, to: "/debts?new=1", key: "debt-new" },
  { label: "Tambah Saldo", icon: Plus, to: "/apps?topup=1", key: "topup" },
  { label: "Rekonsiliasi", icon: ArrowLeftRight, to: "/reconciliation", key: "recon" },
  { label: "Cetak Laporan", icon: FileText, to: "/reports", key: "report" },
];

export default function Dashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hideMoney, setHideMoney] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/dashboard/stats").then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const mask = (v) => (hideMoney ? "•••••" : v);

  if (loading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      </div>
    );
  }

  const t = data.today;
  const s = data.saldo;

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Ringkasan Hari Ini</p>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Dashboard</h1>
        </div>
        <Button
          data-testid="toggle-balance-visibility"
          variant="outline"
          size="sm"
          onClick={() => setHideMoney(!hideMoney)}
          className="gap-2"
        >
          {hideMoney ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          {hideMoney ? "Tampilkan" : "Sembunyikan"}
        </Button>
      </div>

      {/* Hero cards - saldo utama */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card className="p-5 bg-gradient-to-br from-slate-900 to-blue-950 text-white lg:col-span-2 border-0">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-300 font-semibold">Total Aset (Kas + Saldo + Bank)</p>
              <p className="mt-2 money text-3xl lg:text-4xl font-bold tracking-tight">
                {mask(rupiah(s.total_kas + s.total_saldo_apps + s.total_saldo_bank))}
              </p>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-300">
                <span>Kas: <span className="text-white money font-medium">{mask(rupiah(s.total_kas))}</span></span>
                <span>Saldo App: <span className="text-white money font-medium">{mask(rupiah(s.total_saldo_apps))}</span></span>
                <span>Bank: <span className="text-white money font-medium">{mask(rupiah(s.total_saldo_bank))}</span></span>
              </div>
            </div>
            <Banknote className="h-8 w-8 text-blue-400 opacity-60" />
          </div>
          {Math.abs(s.selisih_saldo) > 1000 && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-rose-500/20 border border-rose-500/30 px-3 py-2 text-sm text-rose-200">
              <AlertCircle className="h-4 w-4" />
              Selisih rekonsiliasi total: {rupiah(s.selisih_saldo)}
            </div>
          )}
        </Card>
        <Card className="p-5 border-emerald-200 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-500/5">
          <p className="text-xs uppercase tracking-widest text-emerald-700 dark:text-emerald-400 font-semibold">Laba Bersih Hari Ini</p>
          <p className="mt-2 money text-3xl font-bold text-emerald-700 dark:text-emerald-400 tracking-tight">
            {mask(rupiah(t.total_keuntungan - t.total_pengeluaran))}
          </p>
          <div className="mt-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span>Untung: <span className="money text-foreground font-medium">{mask(rupiahShort(t.total_keuntungan))}</span></span>
            <span>Biaya: <span className="money text-foreground font-medium">{mask(rupiahShort(t.total_pengeluaran))}</span></span>
          </div>
        </Card>
      </div>

      {/* Quick actions */}
      <div>
        <p className="text-sm font-semibold mb-2.5">Aksi Cepat</p>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2.5">
          {QUICK_ACTIONS.map((a) => (
            <Button
              key={a.key}
              data-testid={`quick-${a.key}`}
              variant={a.primary ? "default" : "outline"}
              className={`h-auto py-4 flex-col gap-2 ${a.primary ? "" : "hover:border-primary"}`}
              onClick={() => nav(a.to)}
            >
              <a.icon className={`h-5 w-5 ${a.primary ? "" : "text-primary"}`} />
              <span className="text-xs font-medium leading-none text-center">{a.label}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Today KPIs */}
      <div>
        <p className="text-sm font-semibold mb-2.5">Statistik Hari Ini</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard testid="stat-trx" label="Transaksi" value={t.total_transaksi} icon={Receipt} tone="primary" />
          <StatCard testid="stat-omzet" label="Omzet" value={mask(rupiah(t.total_omzet))} icon={TrendingUp} tone="neutral" />
          <StatCard testid="stat-untung" label="Keuntungan" value={mask(rupiah(t.total_keuntungan))} icon={TrendingUp} tone="success" />
          <StatCard testid="stat-admin" label="Biaya Admin" value={mask(rupiah(t.total_biaya_admin))} icon={Coins} tone="neutral" />
          <StatCard testid="stat-utang-baru" label="Utang Baru" value={mask(rupiah(t.total_utang_baru))} icon={Users} tone="neutral" />
          <StatCard testid="stat-utang-lunas" label="Utang Lunas" value={mask(rupiah(t.total_utang_lunas))} icon={Users} tone="success" />
          <StatCard testid="stat-piutang" label="Piutang Aktif" value={mask(rupiah(t.total_piutang))} icon={AlertCircle} tone="danger" />
          <StatCard testid="stat-pengeluaran" label="Pengeluaran" value={mask(rupiah(t.total_pengeluaran))} icon={Wallet} tone="danger" />
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4">
          <p className="text-sm font-semibold mb-1">Transaksi per Jam (Hari Ini)</p>
          <p className="text-xs text-muted-foreground mb-4">Volume transaksi 24 jam</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.charts.hourly}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="hour" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="count" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-4">
          <p className="text-sm font-semibold mb-1">Keuntungan 7 Hari Terakhir</p>
          <p className="text-xs text-muted-foreground mb-4">Trend keuntungan harian</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.charts.daily_profit}>
              <defs>
                <linearGradient id="p" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(160 84% 39%)" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="hsl(160 84% 39%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => rupiahShort(v)} stroke="hsl(var(--muted-foreground))" />
              <Tooltip formatter={(v) => rupiah(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Area type="monotone" dataKey="profit" stroke="hsl(160 84% 39%)" fill="url(#p)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Apps balances */}
      <div>
        <p className="text-sm font-semibold mb-2.5">Saldo Aplikasi</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {data.apps.map((a) => (
            <Card key={a.id} data-testid={`app-balance-${a.id}`} className="p-3.5 card-hover">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground truncate">{a.type}</p>
                  <p className="font-semibold text-sm truncate">{a.name}</p>
                </div>
                <span className={`h-2 w-2 rounded-full ${a.status === "active" ? "bg-emerald-500" : "bg-slate-400"}`} />
              </div>
              <p className="mt-2 money text-lg font-semibold tracking-tight">{mask(rupiah(a.current_balance))}</p>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
